import React, { useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import './App.css';
import { mealsData as staticMealsData } from './mealsData';
import Login from './Login';
import MealsManager from './MealsManager';
import StaplesManager from './StaplesManager';
import { useWebSocket } from './useWebSocket';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import PullToRefresh from './components/PullToRefresh';
import SwipeableRow from './components/SwipeableRow';
import MenuDrawer from './components/MenuDrawer';
import QuickAddSheet from './components/QuickAddSheet';
import { titleCase } from './constants';
import { stableStringify } from './utils/stableStringify';
import { formatQuantity } from './utils/formatQuantity';
import { hapticLight, hapticSuccess, hapticWarning } from './utils/haptics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });
  const [currentUser, setCurrentUser] = useState(() => {
    return localStorage.getItem('username') || '';
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || '';
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem('isAdmin') === 'true';
  });

  // Navigation state - 'shopping' is the landing view
  const [activeView, setActiveView] = useState('shopping'); // 'shopping', 'planning', 'meals', or 'staples'
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  // Form state tracking - used to disable pull-to-refresh when forms are open
  const [isMealsFormOpen, setIsMealsFormOpen] = useState(false);
  const [isStaplesFormOpen, setIsStaplesFormOpen] = useState(false);

  // Meals data loaded from API
  const [mealsData, setMealsData] = useState(staticMealsData);
  const [mealsLoading, setMealsLoading] = useState(true);

  // User staples loaded from API
  const [userStaples, setUserStaples] = useState([]);
  const [staplesLoading, setStaplesLoading] = useState(true);

  // Set when a meals/staples fetch fails. While either is true the data behind
  // the shopping list is untrustworthy, so we never rebuild from it -- a rebuild
  // against missing meals would silently empty the user's list.
  const [mealsLoadFailed, setMealsLoadFailed] = useState(false);
  const [staplesLoadFailed, setStaplesLoadFailed] = useState(false);

  // State loading flag
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [hasLoadedInitialState, setHasLoadedInitialState] = useState(false);
  
  // Use refs to prevent unnecessary callback recreations and track WS updates
  const isLoadingStateRef = useRef(true);
  const lastReceivedData = useRef({
    mealPlan: null,
    shoppingList: null,
    selectedStaples: null,
    additionalItems: null
  });

  // True once a server snapshot has been applied, from either the WebSocket
  // full_state or the REST GET. Whichever arrives first wins; the loser must not
  // overwrite it with its own, older view of the same state.
  const hasAppliedServerState = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isLoadingStateRef.current = isLoadingState;
  }, [isLoadingState]);

  // Initialize state from cookies or use defaults (will be replaced by backend state)
  const [mealPlan, setMealPlan] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(null);
  const [selectedStaples, setSelectedStaples] = useState(() => {
    if (staticMealsData['Staple']) {
      return staticMealsData['Staple'].reduce((acc, item) => {
        acc[item.ingredient] = true;
        return acc;
      }, {});
    }
    return {};
  });
  const [additionalItems, setAdditionalItems] = useState([]);

  // Track which shopping items have their meal name visible
  const [visibleMeals, setVisibleMeals] = useState({});

  // Ref for shopping list section to enable auto-scroll
  const shoppingListRef = useRef(null);

  // Always-current mirror of shoppingList, so buildShoppingList can read the
  // previous list (to carry checked state forward) without depending on it.
  const latestShoppingList = useRef(shoppingList);
  latestShoppingList.current = shoppingList;

  // Force logout without confirmation (used when JWT expires)
  // Defined early so it can be used by API call functions
  const forceLogout = useCallback(() => {
    console.log('Force logout triggered (JWT expired or auth error)');
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('isAdmin');
    setIsAuthenticated(false);
    setCurrentUser('');
    setToken('');
    setIsAdmin(false);
    setHasLoadedInitialState(false);
    hasAppliedServerState.current = false;
    setActiveView('shopping');
    setIsMenuOpen(false);
    setIsQuickAddOpen(false);

    // Clear all data
    Cookies.remove('mealPlan');
    Cookies.remove('selectedStaples');
    Cookies.remove('additionalItems');
    Cookies.remove('shoppingList');
    setMealPlan([]);
    setShoppingList([]);
    setAdditionalItems([]);
    setMealsData(staticMealsData);
    setUserStaples([]);
    setSelectedStaples({});
  }, []);

  // Handle state updates from WebSocket
  const handleStateUpdate = useCallback((data, field) => {
    console.log('Handling state update from WebSocket:', field || 'full_state');
    
    if (field) {
      // Store the data we received from WebSocket to prevent echo.
      // stableStringify, not JSON.stringify: the server returns keys in sorted
      // order, so a raw stringify never matches our locally built objects.
      lastReceivedData.current[field] = stableStringify(data);
      
      // Partial update from another client
      switch (field) {
        case 'mealPlan':
          console.log('Applying mealPlan update from WebSocket', data);
          setMealPlan(data);
          break;
        case 'shoppingList':
          // Defensive: never let a peer blank out a list we are holding. This
          // client no longer broadcasts empty lists, but an older tab that has
          // not reloaded still can, and losing the list mid-shop is far worse
          // than briefly showing one the user already cleared elsewhere.
          if ((!data || data.length === 0) && latestShoppingList.current.length > 0) {
            console.warn('Ignoring empty shoppingList update - refusing to blank a populated list');
            break;
          }
          setShoppingList(data);
          break;
        case 'selectedStaples':
          setSelectedStaples(data);
          break;
        case 'additionalItems':
          setAdditionalItems(data);
          break;
        default:
          console.warn('Unknown field:', field);
      }
    } else {
      // Full state update from WebSocket - only apply during initial loading.
      // On reconnect we keep local state: the server snapshot may predate edits
      // this device has made but not yet flushed.
      if (isLoadingStateRef.current) {
        console.log('Applying full state from WebSocket');
        hasAppliedServerState.current = true;

        // Store all received data
        lastReceivedData.current.mealPlan = stableStringify(data.mealPlan || []);
        lastReceivedData.current.shoppingList = stableStringify(data.shoppingList || []);
        lastReceivedData.current.selectedStaples = stableStringify(data.selectedStaples || {});
        lastReceivedData.current.additionalItems = stableStringify(data.additionalItems || []);

        setMealPlan(data.mealPlan || []);
        setShoppingList(data.shoppingList || []);
        setSelectedStaples(data.selectedStaples || {});
        setAdditionalItems(data.additionalItems || []);
        setIsLoadingState(false);
        setHasLoadedInitialState(true);
      } else {
        console.log('Ignoring full_state message - already loaded');
      }
    }
  }, []);

  // Initialize WebSocket connection
  const { sendUpdate, isConnected } = useWebSocket(token, handleStateUpdate);

  // Load meals data from API
  const loadMealsFromAPI = useCallback(async () => {
    if (!token) return;
    
    setMealsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/meals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during meals load - logging out');
        forceLogout();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        // Convert user meals array to mealsData object format
        // We only use userMeals for meal planning since users have their own copies
        // Default meals are still available in MealsManager for users to copy if needed
        const userMeals = data.userMeals || [];
        const mealsObject = {};
        
        for (const meal of userMeals) {
          mealsObject[meal.name] = meal.ingredients.map(ing => ({
            ingredient: ing.ingredient,
            quantity: ing.quantity,
            quantity_type: ing.quantity_type,
            department: ing.department
          }));
        }
        
        setMealsData(mealsObject);
        setMealsLoadFailed(false);
        console.log('Loaded meals from API:', Object.keys(mealsObject).length, 'user meals');
      } else {
        console.error('Failed to load meals from API - keeping the meals we already have');
        setMealsLoadFailed(true);
      }
    } catch (error) {
      console.error('Error loading meals from API - keeping the meals we already have:', error);
      setMealsLoadFailed(true);
    }
    setMealsLoading(false);
  }, [token, forceLogout]);

  // Load user staples from API
  const loadStaplesFromAPI = useCallback(async () => {
    if (!token) return;
    
    setStaplesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/staples`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during staples load - logging out');
        forceLogout();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        const loadedStaples = data.staples || [];
        setUserStaples(loadedStaples);
        
        // Initialize selectedStaples for any new staples (use functional update to avoid dependency)
        setSelectedStaples(prev => {
          const newStaples = { ...prev };
          let hasChanges = false;
          for (const staple of loadedStaples) {
            if (newStaples[staple.name] === undefined) {
              newStaples[staple.name] = true;
              hasChanges = true;
            }
          }
          return hasChanges ? newStaples : prev;
        });
        setStaplesLoadFailed(false);
      } else {
        console.error('Failed to load staples from API - keeping the staples we already have');
        setStaplesLoadFailed(true);
      }
    } catch (error) {
      console.error('Error loading staples from API - keeping the staples we already have:', error);
      setStaplesLoadFailed(true);
    }
    setStaplesLoading(false);
  }, [token, forceLogout]);

  // Load meals and staples when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadMealsFromAPI();
      loadStaplesFromAPI();
    }
  }, [isAuthenticated, token, loadMealsFromAPI, loadStaplesFromAPI]);

  // Load initial state from backend on login
  useEffect(() => {
    const loadStateFromBackend = async () => {
      if (!isAuthenticated || !token) {
        setIsLoadingState(false);
        return;
      }

      try {
        console.log('Loading state from backend...');
        const response = await fetch(`${API_URL}/api/state`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Handle JWT expiration
        if (response.status === 401) {
          console.log('JWT expired during state load - logging out');
          forceLogout();
          return;
        }

        if (response.ok) {
          const state = await response.json();
          console.log('State loaded from backend, mealPlan length:', (state.mealPlan || []).length);

          // The WebSocket full_state races this request and usually wins. It is
          // the authoritative snapshot, and updates from other devices may have
          // arrived since. Applying this older REST response on top would undo
          // them, so once the socket has delivered state we only stop the
          // loading screen here.
          if (hasAppliedServerState.current) {
            console.log('Server state already applied over WebSocket - not overwriting with REST response');
            setIsLoadingState(false);
            return;
          }

          hasAppliedServerState.current = true;

          // Set state from backend
          setMealPlan(state.mealPlan || []);
          setShoppingList(state.shoppingList || []);
          // No fallback to the legacy mealsData['Staple'] list here: staples now
          // come from /api/staples, and loadStaplesFromAPI selects any staple it
          // finds that has no saved preference. Reading mealsData would also tie
          // this one-shot state load to the meals fetch.
          setSelectedStaples(state.selectedStaples || {});
          setAdditionalItems(state.additionalItems || []);
          
          // Mark loading complete - DON'T set hasLoadedInitialState yet
          // Let WebSocket or user action set it
          setIsLoadingState(false);
          
          // Only set hasLoadedInitialState if we actually loaded data
          if (state.mealPlan && state.mealPlan.length > 0) {
            console.log('Setting hasLoadedInitialState = true (has data)');
            setHasLoadedInitialState(true);
          } else {
            console.log('NOT setting hasLoadedInitialState (no data yet)');
          }
        } else {
          console.error('Failed to load state:', response.status);
          setIsLoadingState(false);
        }
      } catch (error) {
        console.error('Error loading state from backend:', error);
        setIsLoadingState(false);
      }
    };

    loadStateFromBackend();
  }, [isAuthenticated, token, forceLogout]);

  // Whether there is anything the shopping list can be derived from. Drives both
  // automatic regeneration and the empty-list broadcast guard below.
  const hasShoppingSources = mealPlan.length > 0
    || additionalItems.length > 0
    || Object.values(selectedStaples).some(Boolean);

  // Sync state changes to WebSocket (with debounce handled by hook)
  // Skip if this data is the same as what we just received from WebSocket
  useEffect(() => {
    if (!isConnected || !hasLoadedInitialState || mealPlan.length === 0) {
      return;
    }
    
    const currentData = stableStringify(mealPlan);
    if (currentData === lastReceivedData.current.mealPlan) {
      console.log('useEffect: Skipping mealPlan send (same as WebSocket data)');
      return;
    }
    
    console.log('useEffect: Sending mealPlan update');
    sendUpdate('mealPlan', mealPlan);
  }, [mealPlan, isConnected, hasLoadedInitialState, sendUpdate]);

  useEffect(() => {
    if (!isConnected || !hasLoadedInitialState) {
      return;
    }

    // Never broadcast an empty shopping list. It is always either a transient
    // state before this client rebuilds, or the result of sources that were
    // themselves broadcast on their own channel -- so every other device will
    // reach the empty list on its own. Sending it blanks out other devices and
    // makes them rebuild and rebroadcast, which ping-pongs indefinitely.
    if (shoppingList.length === 0) {
      console.log('useEffect: Skipping empty shoppingList send');
      return;
    }

    const currentData = stableStringify(shoppingList);
    if (currentData === lastReceivedData.current.shoppingList) {
      console.log('useEffect: Skipping shoppingList send (same as WebSocket data)');
      return;
    }

    console.log('useEffect: Sending shoppingList update');
    sendUpdate('shoppingList', shoppingList);
  }, [shoppingList, isConnected, hasLoadedInitialState, sendUpdate]);

  useEffect(() => {
    if (!isConnected || !hasLoadedInitialState) {
      return;
    }
    
    const currentData = stableStringify(selectedStaples);
    if (currentData === lastReceivedData.current.selectedStaples) {
      console.log('useEffect: Skipping selectedStaples send (same as WebSocket data)');
      return;
    }
    
    console.log('useEffect: Sending selectedStaples update');
    sendUpdate('selectedStaples', selectedStaples);
  }, [selectedStaples, isConnected, hasLoadedInitialState, sendUpdate]);

  useEffect(() => {
    if (!isConnected || !hasLoadedInitialState) {
      return;
    }
    
    const currentData = stableStringify(additionalItems);
    if (currentData === lastReceivedData.current.additionalItems) {
      console.log('useEffect: Skipping additionalItems send (same as WebSocket data)');
      return;
    }
    
    console.log('useEffect: Sending additionalItems update');
    sendUpdate('additionalItems', additionalItems);
  }, [additionalItems, isConnected, hasLoadedInitialState, sendUpdate]);

  // Keep cookies as backup (optional - can be removed later)
  useEffect(() => {
    if (mealPlan.length > 0) {
      Cookies.set('mealPlan', JSON.stringify(mealPlan), { expires: 30 });
    }
  }, [mealPlan]);

  useEffect(() => {
    Cookies.set('selectedStaples', JSON.stringify(selectedStaples), { expires: 30 });
  }, [selectedStaples]);

  useEffect(() => {
    Cookies.set('additionalItems', JSON.stringify(additionalItems), { expires: 30 });
  }, [additionalItems]);

  useEffect(() => {
    Cookies.set('shoppingList', JSON.stringify(shoppingList), { expires: 30 });
  }, [shoppingList]);

  // Close dropdown when clicking anywhere on the screen
  useEffect(() => {
    if (showDropdown !== null) {
      const handleClickOutside = (event) => {
        setShowDropdown(null);
      };
      
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const createMealPlan = () => {
    hapticSuccess();
    // TODO: Make number of days configurable by user in settings menu.
    const days = 5;
    const mealNames = Object.keys(mealsData).filter(name => name !== 'Staple');
    const shuffled = [...mealNames].sort(() => Math.random() - 0.5);
    const today = new Date();
    const newMealPlan = [];
    
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      
      const meal = shuffled[i % shuffled.length];
      
      newMealPlan.push({
        date: date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        meal: meal
      });
    }
    
    setMealPlan(newMealPlan);
    setShoppingList([]);
    setAdditionalItems([]);
    
    // Initialize selectedStaples based on userStaples from API
    if (userStaples.length > 0) {
      const defaultStaples = userStaples.reduce((acc, item) => {
        acc[item.name] = true;
        return acc;
      }, {});
      setSelectedStaples(defaultStaples);
    }
    
    // Mark that we've loaded initial state (user action)
    setHasLoadedInitialState(true);
  };

  const handleStartOver = () => {
    const confirmed = window.confirm('Start a new week? This builds a fresh meal plan and clears your current shopping list, checked items, and one-time items.');
    if (confirmed) {
      createMealPlan();
      setActiveView('planning');
    }
  };

  const removeDay = (dayIndex) => {
    hapticLight();
    const updatedMealPlan = mealPlan.filter((_, index) => index !== dayIndex);
    setMealPlan(updatedMealPlan);
  };

  const addDay = () => {
    hapticLight();
    const mealNames = Object.keys(mealsData).filter(name => name !== 'Staple');

    // Select a random meal from all available meals (allow duplicates)
    const selectedMeal = mealNames[Math.floor(Math.random() * mealNames.length)];

    const today = new Date();
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + mealPlan.length);

    const newDay = {
      date: nextDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
      meal: selectedMeal
    };

    setMealPlan([...mealPlan, newDay]);
  };

  const getAvailableMeals = (dayIndex) => {
    const currentMeal = mealPlan[dayIndex].meal;
    const mealNames = Object.keys(mealsData).filter(name => name !== 'Staple');

    // Return all meals except the current one (allow duplicates across different days)
    return mealNames.filter(m => m !== currentMeal);
  };

  const swapMeal = (dayIndex, selectedMeal = null) => {
    hapticLight();
    const availableMeals = getAvailableMeals(dayIndex);

    if (availableMeals.length === 0) {
      hapticWarning();
      alert('No other meals available to swap');
      return;
    }

    const newMeal = selectedMeal || availableMeals[Math.floor(Math.random() * availableMeals.length)];

    const updatedMealPlan = [...mealPlan];
    updatedMealPlan[dayIndex] = { ...updatedMealPlan[dayIndex], meal: newMeal };

    setMealPlan(updatedMealPlan);
    setShowDropdown(null);
  };

  const toggleDropdown = (dayIndex) => {
    hapticLight();
    setShowDropdown(showDropdown === dayIndex ? null : dayIndex);
  };

  const toggleStaple = (ingredientName) => {
    hapticLight();
    setSelectedStaples(prev => ({
      ...prev,
      [ingredientName]: !prev[ingredientName]
    }));
  };

  // Add a one-time item from the quick-add sheet. The sheet owns its own field
  // state and haptics; this just appends to additionalItems, which the
  // regeneration effect picks up and folds into the shopping list.
  const addOneTimeItem = useCallback((newItem) => {
    setAdditionalItems(prev => [...prev, newItem]);
    setHasLoadedInitialState(true);
  }, []);

  // Remove a one-time item by name. The shopping list renders derived rows
  // rather than additionalItems directly, so we map back by name here.
  const removeOneTimeItemByName = useCallback((itemName) => {
    hapticLight();
    setAdditionalItems(prev => {
      const index = prev.findIndex(entry => entry.item === itemName);
      if (index === -1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const toggleMealVisibility = (itemKey) => {
    hapticLight();
    setVisibleMeals(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const toggleShoppingItem = (deptIndex, itemIndex) => {
    hapticLight();
    
    setShoppingList(prevList => {
      // Create a deep copy of the shopping list
      const newList = prevList.map((dept, dIdx) => {
        if (dIdx === deptIndex) {
          return {
            ...dept,
            items: dept.items.map((item, iIdx) => {
              if (iIdx === itemIndex) {
                return {
                  ...item,
                  checked: !item.checked
                };
              }
              return item;
            })
          };
        }
        return dept;
      });
      
      return newList;
    });
  };

  // Pure build: recompute the department-grouped shopping list from the meal
  // plan, selected staples and one-time items. No haptics, no scrolling --
  // this runs automatically whenever its inputs change, including on load.
  const buildShoppingList = useCallback(() => {
    // Read the current list through a ref rather than closing over it, so this
    // callback keeps a stable identity and the effect below only re-runs when
    // the list's actual sources change.
    const previousList = latestShoppingList.current;

    // Carry checked state across the rebuild, keyed by ingredient and source.
    // Counts, not booleans: the same ingredient can legitimately appear more
    // than once under the same key (a meal cooked twice in one week). With a
    // boolean, checking one of those rows would silently check every duplicate
    // on the next rebuild. takeChecked consumes one tick per matching row.
    const checkedCounts = {};
    previousList.forEach(dept => {
      dept.items.forEach(item => {
        if (item.checked) {
          const key = `${item.ingredient}|${item.mealName}`;
          checkedCounts[key] = (checkedCounts[key] || 0) + 1;
        }
      });
    });

    const takeChecked = (key) => {
      if (checkedCounts[key] > 0) {
        checkedCounts[key] -= 1;
        return true;
      }
      return false;
    };

    const allItems = [];

    // Use userStaples from API instead of mealsData['Staple']
    if (userStaples.length > 0) {
      for (const staple of userStaples) {
        const ingName = staple.name;

        if (selectedStaples[ingName]) {
          const mealName = 'Weekly Staple';
          const key = `${ingName}|${mealName}`;
          allItems.push({
            ingredient: ingName,
            quantity: staple.quantity || 'as needed',
            quantity_type: staple.quantity_type || '',
            department: staple.department || 'other',
            isStaple: true,
            isCustom: false,
            mealName: mealName,
            checked: takeChecked(key)
          });
        }
      }
    }

    // Add ingredients from each meal - keep them separate
    for (const day of mealPlan) {
      const mealName = day.meal;
      if (mealsData[mealName]) {
        for (const ingredient of mealsData[mealName]) {
          const key = `${ingredient.ingredient}|${mealName}`;
          allItems.push({
            ingredient: ingredient.ingredient,
            quantity: ingredient.quantity || 'as needed',
            quantity_type: ingredient.quantity_type || '',
            department: ingredient.department || 'other',
            isStaple: false,
            isCustom: false,
            mealName: mealName,
            checked: takeChecked(key)
          });
        }
      }
    }

    // Add custom/additional items
    for (const customItem of additionalItems) {
      const mealName = 'Custom Item';
      const key = `${customItem.item}|${mealName}`;
      allItems.push({
        ingredient: customItem.item,
        quantity: customItem.quantity || 1,
        quantity_type: customItem.quantity_type || '',
        department: customItem.category || 'other',
        isStaple: false,
        isCustom: true,
        mealName: mealName,
        checked: takeChecked(key)
      });
    }

    console.log('Built shopping list:', allItems.length, 'items');


    // Group by department
    const departments = {};
    for (const item of allItems) {
      const dept = item.department;
      if (!departments[dept]) {
        departments[dept] = [];
      }
      departments[dept].push(item);
    }
    
    // Sort items within each department by ingredient name
    for (const dept of Object.keys(departments)) {
      departments[dept].sort((a, b) => a.ingredient.localeCompare(b.ingredient));
    }
    
    const shoppingByDept = Object.keys(departments)
      .sort()
      .map(deptName => ({
        department: deptName,
        items: departments[deptName]
      }));
    
    // We still have sources to build from, yet produced nothing. That means the
    // meals or staples behind them are missing or not loaded yet -- not that the
    // list is genuinely empty. Never wipe a list the user may be shopping from
    // on the strength of data we don't have.
    if (shoppingByDept.length === 0 && previousList.length > 0 && hasShoppingSources) {
      console.warn('Build produced an empty list while sources exist - keeping the current list');
      return;
    }

    // Skip the state write when nothing actually changed, otherwise every
    // automatic rebuild pushes a redundant shoppingList frame over the
    // WebSocket. stableStringify, not JSON.stringify: previousList may have
    // come back from the server with its keys reordered.
    if (stableStringify(shoppingByDept) === stableStringify(previousList)) {
      console.log('Shopping list unchanged - skipping update');
      return;
    }

    setShoppingList(shoppingByDept);
  }, [mealPlan, mealsData, userStaples, selectedStaples, additionalItems, hasShoppingSources]);

  // The list rebuilds itself whenever its sources change, so this only navigates.
  const goToShoppingList = useCallback(() => {
    hapticLight();
    setActiveView('shopping');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Keep the shopping list in sync with its sources. This fires whenever there
  // is anything to build from -- not just when a list already exists -- so the
  // landing view is populated on arrival and a one-time item added to an empty
  // list still shows up.
  useEffect(() => {
    // Only rebuild from data we trust. While meals or staples are still in
    // flight we would be building against the static fallback; if their fetch
    // failed we would be building against a stale or empty collection. Either
    // way the result would not reflect what the user actually has, and writing
    // it would overwrite a list they may be shopping from right now.
    if (mealsLoading || staplesLoading || mealsLoadFailed || staplesLoadFailed) {
      return;
    }

    if (hasLoadedInitialState && hasShoppingSources) {
      buildShoppingList();
    }
  }, [
    hasLoadedInitialState,
    hasShoppingSources,
    mealsLoading,
    staplesLoading,
    mealsLoadFailed,
    staplesLoadFailed,
    buildShoppingList
  ]);

  const handleLoginSuccess = () => {
    const username = localStorage.getItem('username');
    const newToken = localStorage.getItem('token');
    const adminStatus = localStorage.getItem('isAdmin') === 'true';
    setCurrentUser(username);
    setToken(newToken);
    setIsAdmin(adminStatus);
    setIsAuthenticated(true);
    setIsLoadingState(true);
    setHasLoadedInitialState(false); // Reset flag for new login
    setActiveView('shopping'); // Land on the shopping list after login
  };

  const handleLogout = () => {
    const confirmed = window.confirm('Are you sure you want to log out?');
    if (confirmed) {
      forceLogout();
    }
  };

  // Callback to refresh meals after changes in MealsManager
  const handleMealsUpdated = () => {
    loadMealsFromAPI();
  };

  // Callback to refresh staples after changes in StaplesManager
  const handleStaplesUpdated = () => {
    loadStaplesFromAPI();
  };

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    console.log('Pull-to-refresh triggered');
    hapticLight();
    try {
      await Promise.all([
        loadMealsFromAPI(),
        loadStaplesFromAPI()
      ]);
      hapticSuccess();
    } catch (error) {
      console.error('Refresh failed:', error);
      hapticWarning();
    }
  }, [loadMealsFromAPI, loadStaplesFromAPI]);

  // Initialize pull-to-refresh
  const { pullDistance, isRefreshing, progress } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80,
    maxPull: 120,
    disabled: !isAuthenticated || isLoadingState || isMealsFormOpen || isStaplesFormOpen
      || isMenuOpen || isQuickAddOpen
  });

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Show loading screen while loading state
  if (isLoadingState) {
    return (
      <div className="App">
        <div className="loading-screen">
          <h2>Loading your meal plan...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Render the planning view content
  const renderPlanningView = () => {
    // Check if user has any meals (excluding 'Staple' which is legacy)
    const userMealNames = Object.keys(mealsData).filter(name => name !== 'Staple');
    const hasNoMeals = userMealNames.length === 0;

    if (!mealPlan.length) {
      // If user has no meals at all, show a message to add meals first
      if (hasNoMeals && !mealsLoading) {
        return (
          <div className="welcome-section">
            <h2>Welcome to Meal Loop! 🍽️</h2>
            <p>You don't have any meals in your collection yet.</p>
            <p>To get started, go to the <strong>Manage Meals</strong> tab where you can:</p>
            <ul className="welcome-list">
              <li>➕ Create your own custom meals with ingredients</li>
              <li>🔍 Search and add pre-existing meals from our library</li>
            </ul>
            <button
              onClick={() => setActiveView('meals')}
              className="primary-button"
            >
              🍜 Go to Manage Meals
            </button>
          </div>
        );
      }

      return (
        <div className="welcome-section">
          <h2>Welcome!</h2>
          <p>Set up your weekly meal plan.</p>
          <button
            onClick={createMealPlan}
            className="primary-button"
            disabled={mealsLoading}
          >
            {mealsLoading ? 'Loading meals...' : 'Create My Meal Plan'}
          </button>
        </div>
      );
    }

    return (
      <>
        <section className="meal-plan-section">
          <h2>Your Weekly Meal Plan</h2>
          <div className="meal-plan-list">
            {mealPlan.map((day, index) => (
              <SwipeableRow
                key={index}
                onDelete={() => removeDay(index)}
                deleteLabel="Remove"
              >
                <div className="meal-day">
                  <button
                    onClick={() => removeDay(index)}
                    className="remove-day-button"
                    title="Remove this day"
                    aria-label="Remove this day"
                  >
                    ✕
                  </button>
                  <div className="meal-day-info">
                    <span className="day-number">{index + 1}</span>
                    <div className="meal-details">
                      <div className="meal-name">{day.meal}</div>
                    </div>
                  </div>
                  <div className={`meal-actions ${showDropdown === index ? 'dropdown-open' : ''}`}>
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="swap-button"
                    >
                      🔄 Swap
                    </button>
                    {showDropdown === index && (
                      <>
                        <div
                          className="dropdown-backdrop"
                          onClick={() => setShowDropdown(null)}
                        />
                        <div className="swap-dropdown">
                          <button
                            onClick={() => swapMeal(index)}
                            className="dropdown-item random-option"
                          >
                            🎲 I'm Feeling Lucky
                          </button>
                          <div className="dropdown-divider"></div>
                          {getAvailableMeals(index).sort().map((meal, mealIndex) => (
                            <button
                              key={mealIndex}
                              onClick={() => swapMeal(index, meal)}
                              className="dropdown-item"
                            >
                              {meal}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </SwipeableRow>
            ))}
          </div>
          <div className="action-buttons">
            <button
              onClick={addDay}
              className="primary-button"
            >
              + Add Day
            </button>
          </div>
        </section>

        <section className="staples-section">
          <h2>Weekly Staples</h2>
          <p className="staples-description">
            Select which staple items to include in your shopping list.
            <button
              onClick={() => setActiveView('staples')}
              className="manage-staples-link"
            >
              Manage your staples →
            </button>
          </p>
          {staplesLoading ? (
            <div className="staples-loading">Loading staples...</div>
          ) : userStaples.length === 0 ? (
            <div className="no-staples">
              <p>You haven't added any weekly staples yet.</p>
              <button onClick={() => setActiveView('staples')} className="primary-button">
                Add Staples
              </button>
            </div>
          ) : (
            <div className="staples-container">
              {userStaples.map((item, index) => (
                <div key={index} className="staple-item">
                  <input
                    type="checkbox"
                    id={`staple-${index}`}
                    checked={selectedStaples[item.name] || false}
                    onChange={() => toggleStaple(item.name)}
                  />
                  <label htmlFor={`staple-${index}`}>
                    <span className="staple-name">{item.name}</span>
                    <span className="staple-quantity">
                      {formatQuantity(item.quantity, item.quantity_type)}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="generate-section">
          <p className="generate-description">
            Your shopping list updates automatically as you change your meals and staples.
          </p>
          <div className="action-buttons">
            <button
              onClick={goToShoppingList}
              className="generate-button"
            >
              🛒 View Shopping List
            </button>
          </div>
        </section>
      </>
    );
  };

  // Render the shopping list view - this is the landing page
  const renderShoppingView = () => {
    const totalItems = shoppingList.reduce((sum, dept) => sum + dept.items.length, 0);
    const checkedItems = shoppingList.reduce(
      (sum, dept) => sum + dept.items.filter(item => item.checked).length,
      0
    );

    if (totalItems === 0) {
      return (
        <div className="welcome-section">
          <h2>Your shopping list is empty 🛒</h2>
          <p>Set up a meal plan and we'll build the list for you automatically.</p>
          <button
            onClick={() => setActiveView('planning')}
            className="primary-button"
          >
            📅 Set Up Meal Plan
          </button>
          <p className="empty-list-hint">
            Or tap <strong>＋</strong> to add a one-time item.
          </p>
        </div>
      );
    }

    return (
      <section className="shopping-list-section" ref={shoppingListRef}>
        <div className="shopping-list-header">
          <h2>Your Shopping List</h2>
          <span className="shopping-progress">{checkedItems} of {totalItems}</span>
        </div>
        <div className="shopping-list">
          {shoppingList.map((department, deptIndex) => (
            <div key={deptIndex} className="department-section">
              <h3 className="department-header">
                {titleCase(department.department)}
              </h3>
              <div className="department-items">
                {department.items.map((item, itemIndex) => {
                  const qtyStr = formatQuantity(item.quantity, item.quantity_type);
                  const itemKey = `${deptIndex}-${itemIndex}`;
                  const isMealVisible = visibleMeals[itemKey];

                  const row = (
                    <div className="shopping-item">
                      <input
                        type="checkbox"
                        id={`item-${deptIndex}-${itemIndex}`}
                        checked={item.checked || false}
                        onChange={() => toggleShoppingItem(deptIndex, itemIndex)}
                      />
                      <label htmlFor={`item-${deptIndex}-${itemIndex}`}>
                        <span className="ingredient-name">
                          {item.ingredient}
                          {item.isStaple && <span className="staple-badge">Staple</span>}
                          {item.isCustom && <span className="custom-badge">One-time</span>}
                          {!item.isStaple && !item.isCustom && <span
                            className={`meal-badge ${isMealVisible ? 'active' : ''}`}
                            onClick={(e) => {
                              e.preventDefault();
                              toggleMealVisibility(itemKey);
                            }}
                          >
                            Meal
                          </span>}
                          {isMealVisible && (
                            <span className="meal-name-tooltip">{item.mealName}</span>
                          )}
                        </span>
                        <span className="ingredient-quantity">{qtyStr}</span>
                      </label>
                      {item.isCustom && (
                        <button
                          onClick={() => removeOneTimeItemByName(item.ingredient)}
                          className="remove-onetime-button"
                          title="Remove this item"
                          aria-label={`Remove ${item.ingredient}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );

                  // One-time items can be swiped away straight from the list;
                  // everything else is derived from the meal plan or staples
                  // and has to be changed at the source.
                  return item.isCustom ? (
                    <SwipeableRow
                      key={itemIndex}
                      onDelete={() => removeOneTimeItemByName(item.ingredient)}
                      deleteLabel="Remove"
                    >
                      {row}
                    </SwipeableRow>
                  ) : (
                    <React.Fragment key={itemIndex}>{row}</React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="App">
      {/* Pull to Refresh Indicator */}
      <PullToRefresh
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
        threshold={80}
      />
      
      <header className="App-header">
        <div className="header-content">
          <img src="/logo192.png" alt="Meal Loop Logo" className="header-logo" />
          <div className="header-title">
            <h1>Meal Loop</h1>
            <p>Your meals, automated.</p>
          </div>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="menu-button"
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </header>

      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        activeView={activeView}
        onNavigate={(view) => {
          hapticLight();
          setActiveView(view);
          setIsMenuOpen(false);
        }}
        currentUser={currentUser}
        isAdmin={isAdmin}
        onStartOver={() => {
          setIsMenuOpen(false);
          handleStartOver();
        }}
        onLogout={() => {
          setIsMenuOpen(false);
          handleLogout();
        }}
      />

      <main className={`App-main ${activeView === 'shopping' ? 'has-fab' : ''}`}>
        {activeView === 'shopping' && renderShoppingView()}
        {activeView === 'planning' && renderPlanningView()}
        {activeView === 'meals' && (
          <MealsManager
            token={token}
            isAdmin={isAdmin}
            onMealsUpdated={handleMealsUpdated}
            onAuthError={forceLogout}
            onFormStateChange={setIsMealsFormOpen}
          />
        )}
        {activeView === 'staples' && (
          <StaplesManager
            token={token}
            onStaplesUpdated={handleStaplesUpdated}
            onAuthError={forceLogout}
            onFormStateChange={setIsStaplesFormOpen}
          />
        )}
      </main>

      {activeView === 'shopping' && (
        <button
          onClick={() => {
            hapticLight();
            setIsQuickAddOpen(true);
          }}
          className="quick-add-fab"
          aria-label="Add one-time item"
        >
          ＋
        </button>
      )}

      <QuickAddSheet
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onAdd={addOneTimeItem}
      />
    </div>
  );
}

export default App;
